/**
 * semanticExcursionMerge — diff a returned SemanticSnapshot against the
 * excursion's departure baseline. Modeled on the whiteboard round-trip's
 * whiteboardMerge (which is untouched): identity first, then title, then
 * proximity; nothing is ever auto-deleted.
 *
 * Match order (05-VERTICAL-SLICE §VS-2):
 *   0. external id — exact (Miro item ids are stable; survives rename+move)
 *   1. normalized title — survives copy/paste that re-ids an object
 *   2. proximity — position within RENAME_PROXIMITY of an unclaimed
 *      baseline object reads as a rename-in-place
 */

import type { SemanticObject, SemanticRelation, SemanticSnapshot } from './snapshot';

/** Same thresholds the whiteboard merge proved out (normalized [0,1] space). */
export const RENAME_PROXIMITY_THRESHOLD = 0.06;
export const MOVE_DISTANCE_THRESHOLD = 0.08;

export type ObjectChangeStatus = 'unchanged' | 'edited' | 'renamed' | 'moved' | 'new' | 'removed';
export type RelationChangeStatus = 'unchanged' | 'new' | 'retyped' | 'removed';
export type MatchBasis = 'external_id' | 'title' | 'proximity';

export interface MergedObject {
  status: ObjectChangeStatus;
  /** Platform item id, when the object resolves to one (baseline match or export stamp). */
  internalId?: string;
  /** Current external id (absent for removed objects). */
  externalId?: string;
  current?: SemanticObject;
  baseline?: SemanticObject;
  previousTitle?: string;
  matchBasis?: MatchBasis;
}

export interface MergedRelation {
  status: RelationChangeStatus;
  /** Current relation (new / retyped / unchanged). */
  relation?: SemanticRelation;
  /** Baseline relation (retyped / removed). */
  baselineRelation?: SemanticRelation;
  sourceInternalId?: string;
  targetInternalId?: string;
  previousType?: string;
  /**
   * True when both endpoints resolve to platform items — only these can be
   * applied as edges/proposals; the rest surface as observations.
   */
  applicable: boolean;
}

export interface ExcursionMergePlan {
  objects: MergedObject[];
  relations: MergedRelation[];
  summary: {
    unchanged: number;
    edited: number;
    renamed: number;
    moved: number;
    added: number;
    removed: number;
    relationsNew: number;
    relationsRetyped: number;
    relationsRemoved: number;
  };
}

const normalizeTitle = (t: string): string => t.toLowerCase().replace(/\s+/g, ' ').trim();

const distance = (a?: { x: number; y: number }, b?: { x: number; y: number }): number => {
  if (!a || !b) return Number.POSITIVE_INFINITY;
  return Math.hypot(a.x - b.x, a.y - b.y);
};

function contentEdited(current: SemanticObject, baseline: SemanticObject): boolean {
  if ((current.content.description ?? '') !== (baseline.content.description ?? '')) return true;
  const currentCategory = current.asserted.itemCategory?.value;
  const baselineCategory = baseline.asserted.itemCategory?.value;
  if (currentCategory && baselineCategory && currentCategory !== baselineCategory) return true;
  return false;
}

/**
 * The identity of a relation for diffing: resolved endpoint identities
 * (internal id when known, else external id) — order-insensitive for
 * undirected relations.
 */
function relationKey(
  rel: SemanticRelation,
  identityOf: (externalId: string) => string,
): string {
  const a = identityOf(rel.sourceExternalId);
  const b = identityOf(rel.targetExternalId);
  return rel.directionality === 'undirected' && b < a ? `${b}↔${a}` : `${a}→${b}`;
}

export function mergeExcursionSnapshots(
  baseline: SemanticSnapshot,
  current: SemanticSnapshot,
): ExcursionMergePlan {
  const baselineObjects = baseline.objects;
  const currentObjects = current.objects;

  const claimedBaseline = new Set<string>(); // baseline externalIds already matched
  const matches = new Map<string, { baseline: SemanticObject; basis: MatchBasis }>(); // current externalId → match

  // Pass 0 — external id (stable dialect ids; exact across rename + move).
  const baselineByExternal = new Map(baselineObjects.map(o => [o.externalId, o]));
  for (const object of currentObjects) {
    const hit = baselineByExternal.get(object.externalId);
    if (hit && !claimedBaseline.has(hit.externalId)) {
      matches.set(object.externalId, { baseline: hit, basis: 'external_id' });
      claimedBaseline.add(hit.externalId);
    }
  }

  // Pass 1 — normalized title.
  const unclaimed = () => baselineObjects.filter(o => !claimedBaseline.has(o.externalId));
  for (const object of currentObjects) {
    if (matches.has(object.externalId)) continue;
    const title = normalizeTitle(object.content.text);
    if (!title) continue;
    const hit = unclaimed().find(o => normalizeTitle(o.content.text) === title);
    if (hit) {
      matches.set(object.externalId, { baseline: hit, basis: 'title' });
      claimedBaseline.add(hit.externalId);
    }
  }

  // Pass 2 — proximity (a re-ided object near an unclaimed baseline slot
  // reads as a rename-in-place).
  for (const object of currentObjects) {
    if (matches.has(object.externalId)) continue;
    let best: { baseline: SemanticObject; d: number } | null = null;
    for (const candidate of unclaimed()) {
      const d = distance(object.layout, candidate.layout);
      if (d <= RENAME_PROXIMITY_THRESHOLD && (!best || d < best.d)) best = { baseline: candidate, d };
    }
    if (best) {
      matches.set(object.externalId, { baseline: best.baseline, basis: 'proximity' });
      claimedBaseline.add(best.baseline.externalId);
    }
  }

  // Classify objects.
  const objects: MergedObject[] = [];
  for (const object of currentObjects) {
    const match = matches.get(object.externalId);
    if (!match) {
      objects.push({ status: 'new', externalId: object.externalId, current: object });
      continue;
    }
    const base = match.baseline;
    const renamed = normalizeTitle(object.content.text) !== normalizeTitle(base.content.text);
    const edited = contentEdited(object, base);
    const moved = distance(object.layout, base.layout) >= MOVE_DISTANCE_THRESHOLD;
    const status: ObjectChangeStatus = renamed ? 'renamed' : edited ? 'edited' : moved ? 'moved' : 'unchanged';
    objects.push({
      status,
      internalId: base.internalId,
      externalId: object.externalId,
      current: object,
      baseline: base,
      ...(renamed ? { previousTitle: base.content.text } : {}),
      matchBasis: match.basis,
    });
  }
  for (const base of baselineObjects) {
    if (claimedBaseline.has(base.externalId)) continue;
    objects.push({
      status: 'removed',
      internalId: base.internalId,
      baseline: base,
    });
  }

  // Identity resolution for relation endpoints: internal id when the object
  // (or a region matched by title to a baseline object) resolves to one.
  const identityByExternal = new Map<string, string>();
  const internalByExternal = new Map<string, string>();
  for (const merged of objects) {
    if (!merged.externalId) continue;
    const identity = merged.internalId ?? merged.externalId;
    identityByExternal.set(merged.externalId, identity);
    if (merged.internalId) internalByExternal.set(merged.externalId, merged.internalId);
  }
  for (const base of baselineObjects) {
    if (!identityByExternal.has(base.externalId)) {
      identityByExternal.set(base.externalId, base.internalId ?? base.externalId);
      if (base.internalId) internalByExternal.set(base.externalId, base.internalId);
    }
  }
  // Regions ("epic frame" pattern): a frame titled like a known object
  // resolves to that object's item, making frame-containment applicable.
  for (const region of current.regions) {
    if (identityByExternal.has(region.externalId)) continue;
    const title = normalizeTitle(region.title);
    if (!title) continue;
    const byTitle =
      objects.find(o => o.internalId && normalizeTitle(o.current?.content.text ?? o.baseline?.content.text ?? '') === title);
    if (byTitle?.internalId) {
      identityByExternal.set(region.externalId, byTitle.internalId);
      internalByExternal.set(region.externalId, byTitle.internalId);
    }
  }
  const identityOf = (externalId: string): string => identityByExternal.get(externalId) ?? externalId;

  // Diff relations.
  const baselineRelations = new Map(baseline.relations.map(r => [relationKey(r, identityOf), r]));
  const currentRelations = new Map(current.relations.map(r => [relationKey(r, identityOf), r]));

  const relations: MergedRelation[] = [];
  for (const [key, rel] of currentRelations) {
    const base = baselineRelations.get(key);
    const sourceInternalId = internalByExternal.get(rel.sourceExternalId);
    const targetInternalId = internalByExternal.get(rel.targetExternalId);
    // New objects get their internal id at apply time; endpoints matched to
    // *new* objects are applicable (apply resolves ids after item creation).
    const sourceIsObject = current.objects.some(o => o.externalId === rel.sourceExternalId);
    const targetIsObject = current.objects.some(o => o.externalId === rel.targetExternalId);
    const applicable =
      (Boolean(sourceInternalId) || sourceIsObject) && (Boolean(targetInternalId) || targetIsObject);
    if (!base) {
      relations.push({ status: 'new', relation: rel, sourceInternalId, targetInternalId, applicable });
    } else if (base.canonicalType !== rel.canonicalType) {
      relations.push({
        status: 'retyped',
        relation: rel,
        baselineRelation: base,
        previousType: base.canonicalType,
        sourceInternalId,
        targetInternalId,
        applicable,
      });
    } else {
      relations.push({ status: 'unchanged', relation: rel, sourceInternalId, targetInternalId, applicable });
    }
  }
  for (const [key, base] of baselineRelations) {
    if (currentRelations.has(key)) continue;
    relations.push({
      status: 'removed',
      baselineRelation: base,
      sourceInternalId: internalByExternal.get(base.sourceExternalId),
      targetInternalId: internalByExternal.get(base.targetExternalId),
      applicable: false, // removals are flagged, never auto-applied
    });
  }

  const count = (statuses: MergedObject['status'][]) =>
    objects.filter(o => statuses.includes(o.status)).length;
  return {
    objects,
    relations,
    summary: {
      unchanged: count(['unchanged']),
      edited: count(['edited']),
      renamed: count(['renamed']),
      moved: count(['moved']),
      added: count(['new']),
      removed: count(['removed']),
      relationsNew: relations.filter(r => r.status === 'new').length,
      relationsRetyped: relations.filter(r => r.status === 'retyped').length,
      relationsRemoved: relations.filter(r => r.status === 'removed').length,
    },
  };
}
